/**
 * HERMES · DJEN Text Utils
 * -------------------------
 * Sanitização defensiva no cliente. O backend já entrega `texto_display`
 * limpo, mas rows antigas ou paths de ingestão alternativos podem
 * escapar HTML/entities. NUNCA renderizar HTML bruto ao usuário.
 *
 * Regra: sempre passe o texto por `sanitizeDjenText()` antes de exibir.
 */

// Named entities comuns em documentos jurídicos que devem sempre virar
// caractere UTF-8 legível.
const NAMED_ENTITIES = {
  amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", nbsp: " ",
  ordm: "º", ordf: "ª", copy: "©", reg: "®", deg: "°", laquo: "«", raquo: "»",
  aacute: "á", eacute: "é", iacute: "í", oacute: "ó", uacute: "ú",
  Aacute: "Á", Eacute: "É", Iacute: "Í", Oacute: "Ó", Uacute: "Ú",
  agrave: "à", egrave: "è", igrave: "ì", ograve: "ò", ugrave: "ù",
  Agrave: "À", Egrave: "È", Igrave: "Ì", Ograve: "Ò", Ugrave: "Ù",
  atilde: "ã", otilde: "õ", ntilde: "ñ",
  Atilde: "Ã", Otilde: "Õ", Ntilde: "Ñ",
  acirc: "â", ecirc: "ê", icirc: "î", ocirc: "ô", ucirc: "û",
  Acirc: "Â", Ecirc: "Ê", Icirc: "Î", Ocirc: "Ô", Ucirc: "Û",
  auml: "ä", euml: "ë", iuml: "ï", ouml: "ö", uuml: "ü",
  Auml: "Ä", Euml: "Ë", Iuml: "Ï", Ouml: "Ö", Uuml: "Ü",
  ccedil: "ç", Ccedil: "Ç",
  szlig: "ß", eth: "ð", thorn: "þ",
  mdash: "—", ndash: "–", hellip: "…", middot: "·",
  euro: "€", pound: "£", cent: "¢", yen: "¥",
  para: "¶", sect: "§",
  lsquo: "‘", rsquo: "’", ldquo: "“", rdquo: "”",
};

const _looksLikeHtml = (s) =>
  /<(html|body|head|meta|style|script|table|tr|td|div|br|p|b|i|font|article|section)\b/i.test(s || "");

const _hasEntities = (s) => /&(?:[a-z]+|#\d+|#x[0-9a-f]+);/i.test(s || "");

/**
 * decodeHtmlEntities(s): decodifica &ccedil; &#231; &#x00E7; sem double-decode.
 * Usa lookup table para nomeadas comuns + regex para numéricas.
 */
export function decodeHtmlEntities(s) {
  if (!s) return "";
  let out = String(s);
  // Numéricas decimais
  out = out.replace(/&#(\d+);/g, (_, d) => {
    const cp = parseInt(d, 10);
    return Number.isFinite(cp) && cp < 0x110000 ? String.fromCodePoint(cp) : _;
  });
  // Numéricas hex
  out = out.replace(/&#x([0-9a-f]+);/gi, (_, h) => {
    const cp = parseInt(h, 16);
    return Number.isFinite(cp) && cp < 0x110000 ? String.fromCodePoint(cp) : _;
  });
  // Nomeadas
  out = out.replace(/&([a-zA-Z]+);/g, (m, name) =>
    Object.prototype.hasOwnProperty.call(NAMED_ENTITIES, name) ? NAMED_ENTITIES[name] : m
  );
  return out;
}

/**
 * stripHtmlToText(s): remove tags HTML preservando o texto.
 * NÃO usa DOMParser para não executar `<img onerror>` ou similar.
 * Remove blocos de <script>/<style> inteiros (com conteúdo), depois strip tags.
 */
export function stripHtmlToText(s) {
  if (!s) return "";
  let out = String(s);
  // Remove blocos executáveis / estilos inteiros (com conteúdo)
  out = out.replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, " ");
  out = out.replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, " ");
  out = out.replace(/<!--[\s\S]*?-->/g, " ");
  // Substitui tags de bloco por quebras (preserva parágrafos)
  out = out.replace(/<\/?(br|p|div|tr|li|h[1-6]|hr|article|section|table)[^>]*>/gi, "\n");
  // Remove qualquer tag remanescente
  out = out.replace(/<[^>]+>/g, " ");
  return out;
}

/**
 * normalizeWhitespace(s): colapsa múltiplos espaços + limita quebras a 2.
 */
export function normalizeWhitespace(s) {
  if (!s) return "";
  let out = String(s)
    .replace(/[\u200B-\u200D\uFEFF]/g, "")     // zero-width
    .replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F]/g, " ")
    .replace(/[ \t]+/g, " ")
    .replace(/\n{3,}/g, "\n\n");
  const lines = out.split("\n").map((l) => l.trim());
  return lines.join("\n").trim();
}

/**
 * sanitizeDjenText(raw): pipeline defensivo completo.
 * Nunca retorna HTML — sempre texto legível.
 *
 * Ordem: strip HTML → decode entities → normalize whitespace.
 */
export function sanitizeDjenText(raw) {
  if (!raw) return "";
  let out = String(raw);
  if (_looksLikeHtml(out)) out = stripHtmlToText(out);
  if (_hasEntities(out)) out = decodeHtmlEntities(out);
  return normalizeWhitespace(out);
}

/**
 * pickDisplayText(pub): resolve o melhor texto para exibir, sempre limpo.
 * Preferência: texto_display (backend sanitizer) → texto (raw) → "".
 */
export function pickDisplayText(pub) {
  const src = (pub?.texto_display && pub.texto_display.trim()) || pub?.texto || "";
  return sanitizeDjenText(src);
}
